import { t as translate } from "../preferences";
import { LoaderCircle, RefreshCw, Search } from "lucide-react";
import { useRef, useState } from "react";
import { api, type WorkSummary } from "../api";
import { WorkCard } from "../components/Cards";
import { InitialState, InlineError, messageOf, useResource } from "../components/DataState";
import { CompactHeader } from "../components/AppShell";
import { ClientsScreen } from "./ClientsScreen";

const PAGE_SIZE = 20;
type HistoryTab = "works" | "clients";

export function HistoryScreen() {
  const [tab, setTab] = useState<HistoryTab>("works");
  return <div>
    <CompactHeader>
      <div role="tablist" aria-label={translate("Tarix")} className="m-grid m-grid-cols-2 m-gap-1 m-rounded-xl m-bg-elevated m-p-1">
        {([ ["works", translate("Ishlar")], ["clients", translate("Mijozlar")] ] as const).map(([id, label]) => <button key={id} type="button" role="tab" aria-selected={tab === id} className={`m-min-h-9 m-rounded-lg m-px-2 m-text-xs m-font-bold ${tab === id ? "m-bg-surface m-text-primary m-shadow-sm" : "m-text-muted"}`} onClick={() => setTab(id)}>{label}</button>)}
      </div>
    </CompactHeader>
    <div>{tab === "works" ? <WorksTab /> : <ClientsScreen embedded />}</div>
  </div>;
}

function WorksTab() {
  const epoch = useRef(0);
  const loadingPage = useRef(false);
  const [morePending, setMorePending] = useState(false);
  const [moreError, setMoreError] = useState<string | null>(null);
  const [ended, setEnded] = useState(false);
  const [query, setQuery] = useState("");
  const resource = useResource(async () => {
    const request = ++epoch.current;
    loadingPage.current = false; setMorePending(false); setMoreError(null);
    const first = await api.myWorks(PAGE_SIZE, 0);
    if (request === epoch.current) setEnded(first.length < PAGE_SIZE);
    return first;
  }, []);
  async function loadMore() {
    if (!resource.data || resource.loading || loadingPage.current || ended) return;
    const request = epoch.current;
    loadingPage.current = true; setMorePending(true); setMoreError(null);
    try {
      const next = await api.myWorks(PAGE_SIZE, resource.data.length);
      if (request !== epoch.current) return;
      resource.setData((current) => [...(current ?? []), ...next]);
      setEnded(next.length < PAGE_SIZE);
    } catch (error) { if (request === epoch.current) setMoreError(messageOf(error)); }
    finally { if (request === epoch.current) { loadingPage.current = false; setMorePending(false); } }
  }
  const works: WorkSummary[] | null = resource.data;
  const needle = query.trim().toLocaleLowerCase("uz");
  const visible = works?.filter((work) => !needle || `${work.client_name ?? ""} ${work.plate_number ?? ""} ${work.service_name} ${work.note ?? ""}`.toLocaleLowerCase("uz").includes(needle)) ?? [];
  return <>
    <label className="m-relative m-mb-4 m-block"><Search className="m-pointer-events-none m-absolute m-left-3 m-top-3.5 m-size-5 m-text-muted" /><span className="m-sr-only">{translate("Ish qidirish")}</span><input className="m-text-input m-pl-10" value={query} onChange={(event) => setQuery(event.target.value)} placeholder={translate("Mijoz, raqam yoki xizmat")} /></label>
    <InitialState loading={resource.loading} error={resource.error} empty={!resource.loading && !resource.error && works?.length === 0} onRetry={() => void resource.retry()}>{works ? <><InlineError message={resource.error} onRetry={() => void resource.retry()} />{visible.length ? <div className="m-space-y-3">{visible.map((work) => <WorkCard key={work.id} work={work} showMechanicName={false} showParts />)}</div> : <p className="m-rounded-xl m-bg-elevated m-p-4 m-text-sm m-text-muted">{translate("Qidiruv bo‘yicha ish topilmadi.")}</p>}<InlineError message={moreError} onRetry={() => void loadMore()} />{!ended && works.length >= PAGE_SIZE ? <button className="m-secondary-button m-mt-4" onClick={() => void loadMore()} disabled={morePending}>{morePending ? <LoaderCircle className="m-size-5 m-animate-spin" /> : <RefreshCw className="m-size-5" />}{morePending ? translate("Yuklanmoqda…") : translate("Yana ko‘rsatish")}</button> : null}</> : null}</InitialState>
  </>;
}
